import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, { orgsAPI } from '../services/api';
import type { Organization } from '../services/types';

// Platform admin detection — UITL team only
const ADMIN_EMAILS = ['g.kriegler@gmail.com', 'jasens@gmail.com', 'pshofman@gmail.com'];
const ADMIN_DOMAINS = ['uitl.ai'];

function isPlatformAdminFn(email?: string): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  if (ADMIN_EMAILS.includes(lower)) return true;
  const domain = lower.split('@')[1];
  return domain ? ADMIN_DOMAINS.includes(domain) : false;
}

interface User {
  id: number;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  oauth_provider: string | null;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

interface OrgMembershipWithOrg {
  id: number;
  org_id: number;
  role: string;
  organization: Organization;
}

const ADMIN_MODE_KEY = 'pw_admin_mode';
const ADMIN_RESTORE_ACCESS_KEY = 'pw_admin_restore_access';
const ADMIN_RESTORE_REFRESH_KEY = 'pw_admin_restore_refresh';

interface ImpersonatedUser {
  email: string;
  display_name: string | null;
}

interface AuthContextType {
  user: User | null;
  activeOrg: Organization | null;
  activeRole: string | null;
  orgs: Organization[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isPlatformAdmin: boolean;
  showAdmin: boolean;
  adminModeVisible: boolean;
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  setAdminModeVisible: (visible: boolean) => void;
  startImpersonation: (data: {
    access_token: string;
    refresh_token: string;
    admin_restore_access_token: string;
    admin_restore_refresh_token: string;
    impersonated_user: ImpersonatedUser;
    impersonated_org: { id: number; name: string };
  }) => void;
  stopImpersonating: () => void;
  refreshUser: () => Promise<void>;
  switchOrg: (orgId: number) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  activeOrg: null,
  activeRole: null,
  orgs: [],
  isAuthenticated: false,
  isLoading: true,
  isPlatformAdmin: false,
  showAdmin: false,
  adminModeVisible: false,
  isImpersonating: false,
  impersonatedUser: null,
  setAdminModeVisible: () => {},
  startImpersonation: () => {},
  stopImpersonating: () => {},
  refreshUser: async () => {},
  switchOrg: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1];
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [activeOrg, setActiveOrg] = useState<Organization | null>(null);
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadOrgs = useCallback(async () => {
    try {
      const response = await orgsAPI.list();
      const orgList: Organization[] = response.data;
      setOrgs(orgList);

      // Determine active org from JWT
      const token = localStorage.getItem('access_token');
      if (token) {
        const payload = decodeJwtPayload(token);
        const orgId = payload?.org_id;
        const active = orgList.find(o => o.id === orgId) || orgList[0] || null;
        setActiveOrg(active);
      }
    } catch {
      setOrgs([]);
      setActiveOrg(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setActiveOrg(null);
      setOrgs([]);
      return;
    }
    try {
      const response = await api.get('/api/v1/auth/me');
      setUser(response.data);
      await loadOrgs();
    } catch {
      setUser(null);
      setActiveOrg(null);
      setOrgs([]);
    }
  }, [loadOrgs]);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  const switchOrg = async (orgId: number) => {
    try {
      const response = await orgsAPI.switchOrg(orgId);
      const { access_token, refresh_token } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      const org = orgs.find(o => o.id === orgId) || null;
      setActiveOrg(org);
      // Reload to refresh all data with new org context
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch org', err);
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(ADMIN_RESTORE_ACCESS_KEY);
    localStorage.removeItem(ADMIN_RESTORE_REFRESH_KEY);
    setUser(null);
    setActiveOrg(null);
    setOrgs([]);
  };

  // Impersonation state — derived from JWT
  const isImpersonating = useMemo(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    const payload = decodeJwtPayload(token);
    return !!payload?.impersonator_id;
  }, [user]); // re-evaluate when user changes

  const impersonatedUser = useMemo((): ImpersonatedUser | null => {
    if (!isImpersonating || !user) return null;
    return { email: user.email, display_name: user.display_name };
  }, [isImpersonating, user]);

  const startImpersonation = useCallback((data: {
    access_token: string;
    refresh_token: string;
    admin_restore_access_token: string;
    admin_restore_refresh_token: string;
    impersonated_user: ImpersonatedUser;
    impersonated_org: { id: number; name: string };
  }) => {
    // Save admin restore tokens
    localStorage.setItem(ADMIN_RESTORE_ACCESS_KEY, data.admin_restore_access_token);
    localStorage.setItem(ADMIN_RESTORE_REFRESH_KEY, data.admin_restore_refresh_token);
    // Set impersonation tokens
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    window.location.reload();
  }, []);

  const stopImpersonating = useCallback(() => {
    const restoreAccess = localStorage.getItem(ADMIN_RESTORE_ACCESS_KEY);
    const restoreRefresh = localStorage.getItem(ADMIN_RESTORE_REFRESH_KEY);
    localStorage.removeItem(ADMIN_RESTORE_ACCESS_KEY);
    localStorage.removeItem(ADMIN_RESTORE_REFRESH_KEY);
    if (restoreAccess && restoreRefresh) {
      localStorage.setItem('access_token', restoreAccess);
      localStorage.setItem('refresh_token', restoreRefresh);
      window.location.reload();
    } else {
      // Restore tokens missing — force logout
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
  }, []);

  const isPlatformAdmin = useMemo(() => isPlatformAdminFn(user?.email), [user?.email]);

  const [adminModeVisible, setAdminModeVisibleRaw] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(ADMIN_MODE_KEY);
      return stored !== null ? JSON.parse(stored) : false;
    } catch { return false; }
  });

  const setAdminModeVisible = useCallback((visible: boolean) => {
    setAdminModeVisibleRaw(visible);
    localStorage.setItem(ADMIN_MODE_KEY, JSON.stringify(visible));
  }, []);

  const showAdmin = isPlatformAdmin && adminModeVisible;

  return (
    <AuthContext.Provider value={{
      user, activeOrg, activeRole, orgs,
      isAuthenticated: !!user, isLoading, isPlatformAdmin,
      showAdmin, adminModeVisible, setAdminModeVisible,
      isImpersonating, impersonatedUser,
      startImpersonation, stopImpersonating,
      refreshUser, switchOrg, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
